// What a message row can do, wired once for the conversation and the thread pane alike (the Mac's
// full context menu: react, reply in thread, copy, copy link, pin, save, mark unread, edit, delete).
import { useMemo, useRef } from 'react';
import { data } from '../../bridge/client';
import { t } from '../../i18n/i18n';
import { permalinkUrl } from '../router';
import type { RowActions } from './MessageRow';
import { rememberEmoji } from '../features/emoji/recents';
import { openForward } from '../features/compose/composeSignals';
import { copyImageAndSay, saveImageAs } from '../features/attachments/imageClipboard';

/** The shared recents (features/emoji/recents.ts — Apple's `emoji_recents`), so the hover bar learns. */
export function rememberReaction(name: string): void {
  rememberEmoji(name);
}

export function useRowActions(o: {
  onOpenThread(rootId: string): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  quickReactions: string[];
}): RowActions {
  // The parent's callbacks are new on every render of the workspace (a sidebar update, a presence
  // change…); read them at call time so `actions` keeps its identity and the memoized rows do not
  // all re-render with it (perf pass 2026-10-17: 26 row renders per sidebar update).
  const latest = useRef(o);
  latest.current = o;
  return useMemo(
    () => ({
      onReact: (postId, name) => {
        rememberReaction(name);
        void data.call('toggleReaction', postId, name);
      },
      onOpenThread: (rootId) => latest.current.onOpenThread(rootId),
      onOpenUserId: (userId, anchor) => latest.current.onOpenUserId(userId, anchor),
      onRetry: (postId) => void data.call('retryFailed', postId),
      onDiscard: (postId) => void data.call('discardFailed', postId),
      onAction: (postId, actionId, cookie, option) =>
        void data.call('postAction', postId, actionId, cookie, option).then((ok) => {
          if (!ok) latest.current.onToast(t('channel.action.failed'), 'error');
        }),
      onCopyText: (text) => {
        void navigator.clipboard?.writeText(text).then(() => latest.current.onToast(t('web.msg.copied')));
      },
      onCopyLink: (postId) => {
        void navigator.clipboard?.writeText(permalinkUrl(latest.current.teamName, postId)).then(() => latest.current.onToast(t('web.msg.linkCopied')));
      },
      onPin: (postId, pinned) =>
        void data.callUntyped<boolean>('setPostPinned', postId, pinned).then(
          (ok) => ok === false && latest.current.onToast(t('channel.action.failed'), 'error'),
          () => latest.current.onToast(t('channel.action.failed'), 'error'),
        ),
      onSave: (postId, saved) =>
        void data.callUntyped<boolean>('setPostSaved', postId, saved).then(
          (ok) => ok === false && latest.current.onToast(t('channel.action.failed'), 'error'),
          () => latest.current.onToast(t('channel.action.failed'), 'error'),
        ),
      onMarkUnread: (postId) =>
        void data.call('markUnreadFromPost', postId).then((ok) => latest.current.onToast(ok ? t('web.msg.markedUnread') : t('channel.action.failed'), ok ? 'info' : 'error')),
      onEdit: async (postId, message) => {
        const r = await data.call('editPost', postId, message);
        if (!r.ok) latest.current.onToast(r.message || t('channel.action.failed'), 'error');
        return r.ok;
      },
      onDelete: (postId) =>
        void data.call('deletePost', postId).then((r) => {
          if (!r.ok) latest.current.onToast(r.message || t('channel.action.failed'), 'error');
        }),
      onForward: (post, authorName) => openForward({ post, authorName }),
      // Right-click on a picture (features/attachments/imageClipboard): called inside the click.
      onCopyImage: (image) => void copyImageAndSay(image, latest.current.onToast),
      onSaveImage: (image) => saveImageAs(image),
      quickReactions: o.quickReactions,
    }),
    [o.quickReactions],
  );
}
